<template>
  <LoadingSpinner v-if="billLoading || purchaseOrderLoading || workOrderLoading || projectLoading" />
  <div v-else class="edit-bill">
    <PageHeader title="Edit Bill" />

    <div class="mx-auto max-w-7xl">
      <BillUpdateForm
        :model="bill"
        :purchase-order="purchaseOrder"
        :work-order="workOrder"
        :project="project"
        :save-function="updateBill"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Bill } from '@/models/bill'
import BillUpdateForm from '../components/BillUpdateForm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute, useRouter } from 'vue-router'
import WorkOrderService from '@/services/WorkOrderService'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import BillService from '@/services/BillService'
import ProjectService from '@/services/ProjectService'
import { computed } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'

useHead({ title: 'Edit Bill' })

const billService = new BillService()
const workOrderService = new WorkOrderService('')
const purchaseOrderService = new PurchaseOrderService('')
const projectService = new ProjectService()
const queryClient = useQueryClient()
const { notify } = useNotification()
const $router = useRouter()
const $route = useRoute()

const billId = $route.params.id as string || ''

const { data:bill, isLoading:billLoading } = useQuery({
  queryKey: ['bill', billId],
  queryFn: () => billService.find(billId)
})

const enablePOQuery = computed(() => !!bill.value?.purchaseOrderId)
const enableWOQuery = computed(() => !!bill.value?.workOrderId)

const { data:purchaseOrder, isLoading:purchaseOrderLoading } = useQuery({
  queryKey: ['BillPO', bill.value?.purchaseOrderId],
  queryFn: async () => {
    const result = await purchaseOrderService.find_by('purchaseOrderId', '==', bill.value?.purchaseOrderId)
    return result[0]
  },
  enabled: enablePOQuery
})

const { data:workOrder, isLoading:workOrderLoading } = useQuery({
  queryKey: ['BillWO', bill.value?.workOrderId],
  queryFn: async () => {
    const result = await workOrderService.find_by('workOrderId', '==', bill.value?.workOrderId)
    return result[0]
  },
  enabled: enableWOQuery
})

const enableProjectQuery = computed(() => !!bill.value?.projectId)

const { data:project, isLoading:projectLoading } = useQuery({
  queryKey: ['BillPR', bill.value?.projectId],
  queryFn: () => projectService.find(bill.value?.projectId),
  enabled: enableProjectQuery
})

const { mutateAsync: updateBill } = useMutation({
  mutationFn: (bill: Bill) => billService.update(billId, bill),
  onSuccess: (updatedBill: Bill) => {
    queryClient.setQueryData(['bill', updatedBill.id], updatedBill)
    queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })
    notify({
      type: 'success',
      title: 'Bill updated',
      text: 'Bill updated successfully'
    })
    $router.push({ name: 'ShowBill', params: { id: updatedBill.id }, hash: '#showConfirmation' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to update the bill'
    })
  }
})

</script>
